import {
  IconArrowsExchange,
  IconChartFunnel,
  IconClock,
  IconHeart,
  IconLayersIntersect,
  IconLock,
  IconRoute,
  IconSearch,
  IconTargetArrow,
} from "@tabler/icons-react";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";

export const Route = createFileRoute("/_authed/amc/models")({
  component: AmcModelGalleryPage,
});

const MODEL_TEMPLATES = [
  { key: "adReach", icon: IconTargetArrow },
  { key: "conversionPath", icon: IconRoute },
  { key: "crossProduct", icon: IconArrowsExchange },
  { key: "timeToConversion", icon: IconClock },
  { key: "adOverlap", icon: IconLayersIntersect },
  { key: "customerLifetimeValue", icon: IconHeart },
  { key: "searchTerm", icon: IconSearch },
  { key: "marketingFunnel", icon: IconChartFunnel },
] as const;

export function AmcModelGalleryPage(): React.ReactElement {
  const { t } = useTranslation("amc");

  return (
    <main className="flex min-h-full min-w-[1024px] flex-col gap-4 px-6 pb-6 pt-5">
      <Card className="shrink-0 gap-5 py-5 shadow-[0_1px_3px_rgb(10_10_10_/_0.03)]">
        <CardHeader className="px-5">
          <CardTitle className="text-[18px] font-semibold tracking-[-0.02em] text-ink">
            {t("templates.title")}
          </CardTitle>
        </CardHeader>
        <CardContent className="px-5">
          <div className="grid grid-cols-4 gap-3">
            {MODEL_TEMPLATES.map((template) => {
              const Icon = template.icon;
              return (
                <button
                  key={template.key}
                  type="button"
                  disabled
                  aria-label={t(`templates.${template.key}.title`)}
                  className="flex min-h-[132px] cursor-not-allowed flex-col rounded-[12px] border border-line bg-surface p-4 text-left opacity-55"
                >
                  <span className="flex items-start justify-between gap-3">
                    <span className="flex size-8 items-center justify-center rounded-[8px] bg-subtle text-muted-foreground">
                      <Icon className="size-4" stroke={1.75} aria-hidden />
                    </span>
                    <IconLock className="size-4 text-muted-foreground" stroke={1.7} aria-hidden />
                  </span>
                  <span className="mt-3 text-[13px] font-semibold leading-5 text-ink">
                    {t(`templates.${template.key}.title`)}
                  </span>
                  <span className="mt-1 text-[11px] leading-4 text-muted-foreground">
                    {t(`templates.${template.key}.description`)}
                  </span>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Card className="min-h-[480px] flex-1 py-5 shadow-[0_1px_3px_rgb(10_10_10_/_0.03)]">
        <CardHeader className="px-5">
          <CardTitle className="text-[18px] font-semibold tracking-[-0.02em] text-ink">
            {t("myModels.title")}
          </CardTitle>
        </CardHeader>
        <CardContent className="flex min-h-0 flex-1 px-5">
          <Empty className="min-h-0 py-10">
            <EmptyHeader>
              <EmptyMedia variant="icon" className="mb-3 text-muted-foreground">
                <IconLock className="size-5" aria-hidden />
              </EmptyMedia>
              <EmptyTitle className="text-[16px]">{t("myModels.unavailableTitle")}</EmptyTitle>
              <EmptyDescription>{t("myModels.unavailableDescription")}</EmptyDescription>
            </EmptyHeader>
          </Empty>
        </CardContent>
      </Card>
    </main>
  );
}
